import { useMemo, useState } from "react";
import type { Ref } from "react";
import { Field } from "@design-system/Field/Field";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useWorkspaceEntities } from "@hooks/useWorkspaceEntities";
import { WorkspaceScopedReportSettingsTarget } from "@common/helpers/report-settings/report-setting-candidates";
import { useWorkspaceReportSettings } from "@api/reportSettings.api";
import { ReportSettingsEditorHandle } from "../ReportSettingsEditorDrawer";
import styles from "../ReportSettings.module.scss";
import { getTemplateNames } from "../reportSettingsSourceCopy";
import { LayoutEditorLoadState } from "./LayoutEditorLoadState";
import { LayoutEditorShell, getUnsavedLabel } from "./LayoutEditorShell";
import { useReportSettingsDrafts } from "./useReportSettingsDrafts";

interface Props {
    workspaceId: string;
    initialEntityId?: number;
    isOpen: boolean;
    onRequestClose: () => void;
}

const WORKSPACE_LEVEL = 0;
const WORKSPACE_DEFAULT_LABEL = "Client default";
const WORKSPACE_DEFAULT_TOOLTIP =
    "Set the default for every entity in this client. Select a specific entity to override its settings.";

export const LayoutEditor = ({
    workspaceId,
    initialEntityId,
    isOpen,
    onRequestClose,
    ref,
}: Props & { ref?: Ref<ReportSettingsEditorHandle> }) => {
    const { data, isPending, isFetching, refetch } =
        useWorkspaceReportSettings(workspaceId);
    const drafts = useReportSettingsDrafts({ workspaceId });
    const [selectedEntityId, setSelectedEntityId] = useState<number | null>(
        initialEntityId ?? null,
    );
    const { entities } = useWorkspaceEntities(workspaceId);
    const appEntities = useMemo(
        () => entities.filter((entity) => entity.isBusiness),
        [entities],
    );

    const target = useMemo<WorkspaceScopedReportSettingsTarget>(
        () =>
            selectedEntityId === null
                ? { kind: "workspace" }
                : { kind: "entity", entityId: selectedEntityId },
        [selectedEntityId],
    );

    if (!data) {
        return (
            <LayoutEditorLoadState
                isPending={isPending || isFetching}
                onRetry={() => void refetch()}
            />
        );
    }

    const selectedAppEntity = appEntities.find(
        (entity) => entity.id === selectedEntityId,
    );
    const selectedEntityName = data.entities.find(
        (entity) => entity.id === selectedEntityId,
    )?.name;
    const workspaceItem = {
        value: WORKSPACE_LEVEL,
        label: WORKSPACE_DEFAULT_LABEL,
        tooltip: WORKSPACE_DEFAULT_TOOLTIP,
        rightText: getUnsavedLabel(
            drafts.hasUnsavedEditsForTarget({ kind: "workspace" }),
        ),
    };
    const entityItems = data.entities.map((entity) => {
        const appEntity = appEntities.find(({ id }) => id === entity.id);
        return {
            value: entity.id,
            label: entity.name ?? "",
            leading: appEntity ? (
                <EntityAvatar entity={appEntity} size="xs" aria-hidden />
            ) : undefined,
            rightText: getUnsavedLabel(
                drafts.hasUnsavedEditsForTarget({
                    kind: "entity",
                    entityId: entity.id,
                }),
            ),
        };
    });
    const entityPicker = (
        <Field label="Entity" control="button">
            <Tooltip
                content={
                    selectedEntityId === null
                        ? WORKSPACE_DEFAULT_TOOLTIP
                        : undefined
                }
            >
                <span className={styles.pickerTooltipTrigger}>
                    <Picker.Select
                        sections={[
                            { key: "default", items: [workspaceItem] },
                            { key: "entities", items: entityItems },
                        ]}
                        searchable
                        value={selectedEntityId ?? WORKSPACE_LEVEL}
                        onValueChange={(value) =>
                            setSelectedEntityId(
                                value !== null && value !== WORKSPACE_LEVEL
                                    ? value
                                    : null,
                            )
                        }
                        renderValue={() =>
                            selectedAppEntity ? (
                                <EntityDisplay entity={selectedAppEntity} />
                            ) : (
                                <span>
                                    {selectedEntityName ??
                                        WORKSPACE_DEFAULT_LABEL}
                                </span>
                            )
                        }
                        trigger={<Picker.Button variant="primary" size="xl" />}
                    />
                </span>
            </Tooltip>
        </Field>
    );

    const entityTemplate =
        target.kind === "entity"
            ? data.entities.find((entity) => entity.id === target.entityId)
                  ?.template
            : undefined;
    const previewedEntity =
        data.entities.find((entity) => entity.id === selectedEntityId) ??
        data.entities[0];
    const canEdit =
        target.kind === "workspace" ||
        data.entities.some(
            (entity) => entity.id === target.entityId && entity.canEdit,
        );

    return (
        <LayoutEditorShell
            ref={ref}
            drafts={drafts}
            target={target}
            targetKey={String(selectedEntityId ?? "workspace")}
            scope={{
                workspaceId,
                entityTemplate,
                workspaceTemplate: data.workspaceTemplate,
                firmDefaultTemplate: data.firmDefaultTemplate,
            }}
            templateNames={getTemplateNames(data)}
            canEdit={canEdit}
            entityPicker={entityPicker}
            entityName={previewedEntity?.name ?? null}
            firmName={data.brandingFirm?.name ?? null}
            firmLogoUrl={data.brandingFirm?.logoUrl ?? null}
            entityLogoUrl={
                appEntities.find(({ id }) => id === previewedEntity?.id)
                    ?.logo ?? null
            }
            isOpen={isOpen}
            onRequestClose={onRequestClose}
        />
    );
};
